import { PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Tabs } from 'antd';
import React from 'react';
import { defaultTablePagination, DRAWER_WIDTH } from '../../config/constants';
import Routes from '../../config/routes';
import { translations } from '../../config/translations';
import { ActionType, DrawerState, ModuleName } from '../../core/models/enum';
import { authorizeAction } from '../../helpers/CheckPermissionHelper';
import { ToolDetailVm } from '../../utils/api';
import ToolForm from './tool-form/ToolForm';
import ToolTable from './tool-table';
import { Props } from './index';

interface State {
    allowCreate: boolean;
    drawerState: DrawerState;
}

class Tools extends React.Component<Props, State> {
    private tableRef: any = React.createRef();

    public constructor(props: Props) {
        super(props);

        const {
            match: { path },
        } = props;

        this.state = {
            allowCreate: authorizeAction(props.userProfile, ModuleName.Tools, ActionType.Create),
            drawerState: path === Routes.ROUTE_TOOLS_NEW ? DrawerState.Create : DrawerState.Closed,
        };
    }

    // The drawer follows the route, so Back/Forward open and close it too.
    public componentDidUpdate = (prevProps: Props) => {
        const { match } = this.props;

        if (prevProps.match.path !== match.path) {
            this.setState({
                drawerState:
                    match.path === Routes.ROUTE_TOOLS_NEW ? DrawerState.Create : DrawerState.Closed,
            });
        }
    };

    private handleDrawerMode = (drawerState: DrawerState) => {
        const { history, location } = this.props;

        this.setState({
            drawerState,
        });

        // Keep the list's filters, tab and page in the URL across opening and closing the drawer.
        history.push({
            pathname:
                drawerState === DrawerState.Create ? Routes.ROUTE_TOOLS_NEW : Routes.ROUTE_TOOLS,
            search: location.search,
        });
    };

    private handleSuccessCreate = (_: ToolDetailVm) => {
        this.handleDrawerMode(DrawerState.Closed);
        this.tableRef.current.getTools(defaultTablePagination);
    };

    private handleTabChange = async (activeKey: string) => {
        const { history, location } = this.props;
        const params = new URLSearchParams(location.search);

        if (params.has('activeTab')) {
            params.set('activeTab', activeKey);
        } else {
            params.append('activeTab', activeKey);
        }

        // The other tab has its own page count; staying on page 4 would show an empty page.
        params.set('pageIndex', '1');

        history.push({
            pathname: history.location.pathname,
            search: params.toString(),
        });
    };

    public render(): React.ReactElement {
        const { drawerState, allowCreate } = this.state;
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const activeTab = params.get('activeTab') ? params.get('activeTab') : 'active';

        return (
            <>
                <PageHeader
                    title={translations.sidebar.toolsAndEquipment}
                    extra={
                        allowCreate
                            ? [
                                  <Button
                                      key="1"
                                      type="primary"
                                      style={{
                                          zIndex: 10,
                                          float: 'right',
                                      }}
                                      onClick={() => this.handleDrawerMode(DrawerState.Create)}
                                      className="no-print"
                                  >
                                      <PlusOutlined />
                                      {translations.tools.addToolEquipment}
                                  </Button>,
                              ]
                            : []
                    }
                >
                    <Tabs
                        type="editable-card"
                        activeKey={activeTab || 'active'}
                        onTabClick={(key: string) => this.handleTabChange(key)}
                        hideAdd
                        destroyInactiveTabPane={true}
                        items={[
                            { label: translations.employees.activeTab, key: 'active', closable: false },
                            {
                                label: translations.news.archived,
                                key: 'archived',
                                closable: false,
                            },
                        ]}
                    />
                </PageHeader>

                <ToolTable wrappedComponentRef={this.tableRef} />

                <Drawer
                    title={translations.tools.addToolEquipment}
                    open={!!drawerState}
                    onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    <ToolForm
                        onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                        onSuccess={this.handleSuccessCreate}
                    />
                </Drawer>
            </>
        );
    }
}

export default Tools;

